/**
 * Cabeçalho do perfil do aluno: avatar com iniciais + nome.
 *
 * Em modo edição mostra TextInput pro full_name; senão mostra texto.
 * Iniciais sempre derivadas do `full_name ?? email` (até 2 letras).
 *
 * Props:
 *   - student: aluno (pra calcular iniciais e mostrar nome em modo view)
 *   - isEditing: alterna entre Text e TextInput
 *   - editName: valor controlado do input (só usado em edição)
 *   - onChangeName: setter chamado a cada keystroke do input
 *   - showEditButton: quando true, renderiza um botão "Editar perfil"/
 *     "Cancelar" inline abaixo do nome. Usado em desktop, onde o lápis
 *     do header não existe (DesktopShell não monta Stack header).
 *   - onToggleEdit: handler do botão (alterna isEditing no orquestrador)
 *
 * Quando NÃO usar: este componente é específico do perfil do aluno —
 * para outros cabeçalhos com avatar, prefira criar um novo.
 */

import { memo } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Student } from '@/lib/types';
import { sharedStyles } from './styles';

interface Props {
  student: Student;
  isEditing: boolean;
  editName: string;
  onChangeName: (value: string) => void;
  showEditButton?: boolean;
  onToggleEdit?: () => void;
}

function ProfileHeaderImpl({
  student, isEditing, editName, onChangeName, showEditButton, onToggleEdit,
}: Props) {
  const initials = (student.full_name ?? student.email)
    .split(' ')
    .map(n => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <View style={styles.header}>
      <View style={styles.avatar}>
        <Text style={styles.initials}>{initials}</Text>
      </View>
      {isEditing ? (
        <TextInput
          style={[sharedStyles.input, styles.nameInput]}
          value={editName}
          onChangeText={onChangeName}
        />
      ) : (
        <Text style={styles.name}>{student.full_name ?? student.email}</Text>
      )}
      {showEditButton && onToggleEdit && (
        <TouchableOpacity style={styles.editBtn} onPress={onToggleEdit}>
          <Ionicons
            name={isEditing ? 'close-outline' : 'pencil-outline'}
            size={16}
            color="#C8102E"
          />
          <Text style={styles.editBtnText}>
            {isEditing ? 'Cancelar' : 'Editar perfil'}
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  header: { alignItems: 'center', paddingVertical: 20 },
  avatar: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: '#C8102E',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  initials: { color: '#fff', fontWeight: 'bold', fontSize: 28 },
  name: { fontSize: 20, fontWeight: 'bold', color: '#1A1A1A' },
  nameInput: { textAlign: 'center', fontSize: 18, fontWeight: '600' },
  editBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 10,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: '#C8102E',
  },
  editBtnText: { color: '#C8102E', fontSize: 13, fontWeight: '600' },
});

export const ProfileHeader = memo(ProfileHeaderImpl);
